import React, { useState, useRef, lazy } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Lock, Gift, MapPin, ChevronLeft, Bell, BellOff, CheckCircle, Flame, ArrowLeft, ShoppingBag, Image as ImageIcon, Mic, Flag, Target, Music, Award, DollarSign, Send, ChevronRight } from 'lucide-react';
interface BellaBlueAngelFeedProps {
  onBack: () => void;
}
export const BellaBlueAngelFeed: React.FC<BellaBlueAngelFeedProps> = ({
  onBack
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [isFollowing, setIsFollowing] = useState(false);
  const [likedPosts, setLikedPosts] = useState<Record<string, boolean>>({});
  const [showNotificationOptions, setShowNotificationOptions] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [showPollResults, setShowPollResults] = useState(false);
  const [pollVote, setPollVote] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPostId, setUnlockPostId] = useState<string | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabs = [{
    id: 'all',
    label: 'All',
    icon: <Flame size={14} data-id="element-2029" />
  }, {
    id: 'media',
    label: 'Media',
    icon: <ImageIcon size={14} data-id="element-2030" />
  }, {
    id: 'voice',
    label: 'Voice',
    icon: <Mic size={14} data-id="element-2031" />
  }, {
    id: 'missions',
    label: 'Missions',
    icon: <Target size={14} data-id="element-2032" />
  }, {
    id: 'moans',
    label: 'Moans',
    icon: <Music size={14} data-id="element-2033" />
  }, {
    id: 'polls',
    label: 'Polls',
    icon: <Flag size={14} data-id="element-2034" />
  }, {
    id: 'upsells',
    label: 'Upsells',
    icon: <Lock size={14} data-id="element-2035" />
  }, {
    id: 'rewards',
    label: 'Rewards',
    icon: <Award size={14} data-id="element-2036" />
  }, {
    id: 'shop',
    label: 'Shop',
    icon: <ShoppingBag size={14} data-id="element-2037" />
  }];
  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      tabsRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const toggleFollow = () => {
    setIsFollowing(!isFollowing);
  };
  const toggleNotification = () => {
    setNotificationEnabled(!notificationEnabled);
    setShowNotificationOptions(false);
  };
  const toggleLike = (postId: string) => {
    setLikedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };
  const handlePollVote = (option: string) => {
    setPollVote(option);
    setShowPollResults(true);
  };
  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would submit the comment
    setCommentText('');
  };
  const handleUnlock = (postId: string) => {
    setUnlockPostId(postId);
    setShowUnlockModal(true);
  };
  const confirmUnlock = () => {
    // Here you would handle the actual token transaction
    setShowUnlockModal(false);
  };
  const posts = [{
    id: 'post1',
    type: 'premium_content',
    content: 'Just finished my workout... want to see how sweaty I got? 💦 My blue eyes look even more intense after pushing my limits...',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b3.png',
    likes: 3452,
    comments: 287,
    time: '1h ago',
    locked: true,
    price: 9,
    location: 'Gym',
    hashtags: ['#fitness', '#workout', '#blueeyes', '#exclusive']
  }, {
    id: 'post2',
    type: 'poll',
    content: "What workout should I do on my next livestream? I'll be wearing your favorite outfit... 💙",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b3.png',
    likes: 1789,
    comments: 312,
    time: '4h ago',
    location: 'Planning My Routine',
    poll: {
      options: [{
        text: 'Yoga in tight leggings 🧘‍♀️',
        percentage: 60
      }, {
        text: 'Cardio in shorts & sports bra 🏃‍♀️',
        percentage: 40
      }],
      endsIn: '3 hours'
    },
    hashtags: ['#fitnesspoll', '#workoutstream', '#helpmedecide', '#gymlife']
  }, {
    id: 'post3',
    type: 'voice_note',
    content: 'Listen to how breathless I get during my intense workouts... some sounds are just too good to miss 🎧',
    likes: 2231,
    comments: 172,
    time: '1d ago',
    location: 'Cardio Session',
    audioWaveform: [0.2, 0.5, 0.3, 0.8, 0.4, 0.6, 0.3, 0.5, 0.7, 0.8, 0.5, 0.3, 0.6, 0.4, 0.2],
    duration: '0:47',
    hashtags: ['#voice', '#workout', '#asmr', '#heavybreathing']
  }];
  // Filter posts based on active tab
  const filteredPosts = posts.filter(post => {
    if (activeTab === 'all') return true;
    if (activeTab === 'media' && (post.type === 'image' || post.type === 'premium_content')) return true;
    if (activeTab === 'voice' && post.type === 'voice_note') return true;
    if (activeTab === 'polls' && post.type === 'poll') return true;
    if (activeTab === 'upsells' && post.type === 'premium_content') return true;
    return false;
  });
  return <section className="w-full max-w-[860px] mx-auto px-4 space-y-4 angel-feed-container" data-id="element-2038">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-950/90 backdrop-blur-sm p-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-800" data-id="element-2039">
        <div className="flex items-center gap-4" data-id="element-2040">
          <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" onClick={onBack} data-id="element-2041">
            <ArrowLeft size={20} className="text-gray-700 dark:text-gray-300" data-id="element-2042" />
          </button>
          <div data-id="element-2043">
            <h1 className="font-bold text-lg text-gray-900 dark:text-white" data-id="element-2044">
              Bella Blue
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2045">
              1.8M Followers
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-2046">
          <div className="relative" data-id="element-2047">
            <button className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700" onClick={() => setShowNotificationOptions(!showNotificationOptions)} data-id="element-2048">
              {notificationEnabled ? <Bell size={20} className="text-blue-500 dark:text-blue-400" data-id="element-2049" /> : <BellOff size={20} className="text-gray-500 dark:text-gray-400" data-id="element-2050" />}
            </button>
            {showNotificationOptions && <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 z-10" data-id="element-2051">
                <button className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-lg flex items-center gap-2 text-gray-700 dark:text-gray-300" onClick={toggleNotification} data-id="element-2052">
                  {notificationEnabled ? <>
                      <BellOff size={16} data-id="element-2053" />
                      <span data-id="element-2054">Disable notifications</span>
                    </> : <>
                      <Bell size={16} data-id="element-2055" />
                      <span data-id="element-2056">Enable notifications</span>
                    </>}
                </button>
              </div>}
          </div>
          <button className={`px-4 py-1.5 rounded-full font-bold ${isFollowing ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white hover:bg-red-100 dark:hover:bg-red-900 hover:text-red-600 dark:hover:text-red-400' : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90'}`} onClick={toggleFollow} data-id="element-2057">
            {isFollowing ? 'Following' : 'Follow'}
          </button>
        </div>
      </div>

      {/* Profile Banner */}
      <div className="relative" data-id="element-2058">
        <div className="h-32 w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-xl" data-id="element-2059"></div>
        <div className="absolute -bottom-16 left-4 w-32 h-32 rounded-full border-4 border-white dark:border-gray-950 overflow-hidden" data-id="element-2060">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b3.png" alt="Bella Blue" className="w-full h-full object-cover image-high-quality" loading="lazy" data-id="element-2061" />
        </div>
      </div>

      {/* Profile Info */}
      <div className="mt-20 px-4 ml-36" data-id="element-2062">
        <div className="flex items-center gap-1" data-id="element-2063">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2064">
            Bella Blue
          </h2>
          <CheckCircle size={16} className="text-blue-400" fill="currentColor" data-id="element-2065" />
          <span className="text-sm font-normal text-blue-400 ml-2" data-id="element-2066">💙🔥</span>
        </div>
        <p className="text-gray-500 dark:text-gray-400" data-id="element-2067">@BellaBlueEyes</p>
        <p className="mt-2 text-gray-700 dark:text-gray-300" data-id="element-2068">
          Just a blue-eyed girl with a wild side 💙 Fitness enthusiast &
          aspiring model. My eyes will captivate you, my body will keep you
          coming back...
        </p>
        <div className="flex gap-4 mt-3 text-sm" data-id="element-2069">
          <div className="flex items-center gap-1" data-id="element-2070">
            <span className="font-bold text-gray-900 dark:text-white" data-id="element-2071">108</span>
            <span className="text-gray-500 dark:text-gray-400" data-id="element-2072">Following</span>
          </div>
          <div className="flex items-center gap-1" data-id="element-2073">
            <span className="font-bold text-gray-900 dark:text-white" data-id="element-2074">
              1.8M
            </span>
            <span className="text-gray-500 dark:text-gray-400" data-id="element-2075">Followers</span>
          </div>
          <div className="flex items-center gap-1" data-id="element-2076">
            <span className="font-bold text-gray-900 dark:text-white" data-id="element-2077">385</span>
            <span className="text-gray-500 dark:text-gray-400" data-id="element-2078">
              Intimacy XP
            </span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mt-4 relative border-b border-gray-200 dark:border-gray-800" data-id="element-2079">
        <button className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 rounded-full text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" onClick={() => scrollTabs('left')} data-id="element-2080">
          <ChevronLeft size={18} data-id="element-2081" />
        </button>
        <div ref={tabsRef} className="flex overflow-x-auto scrollbar-hide px-8 py-1" data-id="element-2082">
          {tabs.map(tab => <button key={tab.id} className={`px-4 py-2 flex items-center gap-1.5 whitespace-nowrap rounded-full mx-1 ${activeTab === tab.id ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'}`} onClick={() => setActiveTab(tab.id)} data-id="element-2083">
              {tab.icon}
              <span data-id="element-2084">{tab.label}</span>
            </button>)}
        </div>
        <button className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 rounded-full text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" onClick={() => scrollTabs('right')} data-id="element-2085">
          <ChevronRight size={18} data-id="element-2086" />
        </button>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4" data-id="element-2087">
        {filteredPosts.length > 0 ? filteredPosts.map(post => <div key={post.id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-in fade-in duration-300 angel-feed-post shadow-md hover:shadow-xl transition-all duration-300" data-id="element-2088">
              {/* Post Header */}
              <div className="p-3 flex items-center gap-2 border-b border-gray-200 dark:border-gray-800" data-id="element-2089">
                <div className="w-10 h-10 rounded-full overflow-hidden" data-id="element-2090">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b3.png" alt="Bella Blue" className="w-full h-full object-cover image-high-quality" loading="lazy" data-id="element-2091" />
                </div>
                <div data-id="element-2092">
                  <div className="flex items-center gap-1" data-id="element-2093">
                    <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-2094">
                      Bella Blue
                    </h3>
                    <CheckCircle size={14} className="text-blue-400" fill="currentColor" data-id="element-2095" />
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2096">
                      • {post.time}
                    </span>
                  </div>
                  {post.location && <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2097">
                      <MapPin size={10} className="ml-1" data-id="element-2098" />
                      <span data-id="element-2099">{post.location}</span>
                    </div>}
                </div>
                <button className="ml-auto" data-id="element-2100">
                  <MoreHorizontal size={16} className="text-gray-500 dark:text-gray-400" data-id="element-2101" />
                </button>
              </div>

              {/* Post Content */}
              <div className="px-3 py-2" data-id="element-2102">
                <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2103">
                  {post.content}
                </p>
                {/* Hashtags */}
                {post.hashtags && <div className="text-xs text-blue-500 dark:text-blue-400 mt-1" data-id="element-2104">
                    {post.hashtags.join(' ')}
                  </div>}
              </div>

              {/* Post Media */}
              {(post.type === 'image' || post.type === 'poll' && post.image) && post.image && <div className="aspect-square bg-gray-100 dark:bg-gray-800" data-id="element-2105">
                    <img src={post.image} alt="Post" className="w-full h-full object-cover angel-post-image hover:scale-[1.02] transition-transform duration-300" sizes="(max-width: 768px) 100vw, 820px" loading="lazy" data-id="element-2106" />
                  </div>}

              {/* Premium Content */}
              {post.type === 'premium_content' && post.image && <div className="relative aspect-square bg-gray-100 dark:bg-gray-800" data-id="element-2107">
                  <img src={post.image} alt="Locked Content" className="w-full h-full object-cover blur-sm angel-post-image" sizes="(max-width: 768px) 100vw, 820px" loading="lazy" data-id="element-2108" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-2109">
                    <Lock size={32} className="text-blue-400 mb-2" data-id="element-2110" />
                    <p className="text-center text-white font-medium" data-id="element-2111">
                      Unlock Exclusive Photo
                    </p>
                    <button className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full text-white font-medium flex items-center gap-2 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300" onClick={() => handleUnlock(post.id)} data-id="element-2112">
                      <span data-id="element-2113">Unlock for</span>
                      <span className="text-blue-200" data-id="element-2114">{post.price} 💎</span>
                    </button>
                  </div>
                </div>}

              {/* Poll */}
              {post.type === 'poll' && post.poll && <div className="p-3" data-id="element-2115">
                  <div className="space-y-2" data-id="element-2116">
                    {post.poll.options.map((option, i) => <button key={i} className={`w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border ${pollVote === option.text ? 'border-blue-500 dark:border-blue-400' : 'border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400'} transition flex justify-between items-center`} onClick={() => handlePollVote(option.text)} data-id="element-2117">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-2118">
                          {option.text}
                        </span>
                        {(showPollResults || pollVote) && <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full text-gray-700 dark:text-gray-300" data-id="element-2119">
                            {option.percentage}%
                          </span>}
                      </button>)}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-2120">
                      Voting ends in {post.poll.endsIn} • Results will shape my
                      next workout stream
                    </p>
                  </div>
                </div>}

              {/* Voice Note */}
              {post.type === 'voice_note' && post.audioWaveform && <div className="p-3" data-id="element-2121">
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2122">
                    <div className="flex items-center gap-3" data-id="element-2123">
                      <button className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300" data-id="element-2124">
                        <div className="ml-1" data-id="element-2125">▶</div>
                      </button>
                      <div className="flex-grow h-10 flex items-center" data-id="element-2126">
                        {post.audioWaveform.map((level, i) => <div key={i} className="w-1 mx-0.5 rounded-full bg-gradient-to-t from-blue-500/50 to-indigo-500/50" style={{
                  height: `${level * 32}px`
                }} data-id="element-2127"></div>)}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2128">
                        <span data-id="element-2129">{post.duration}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-2130">
                      🎧 Best experienced with headphones
                    </p>
                  </div>
                </div>}

              {/* Post Actions */}
              <div className="p-3" data-id="element-2131">
                <div className="flex items-center justify-between" data-id="element-2132">
                  <div className="flex items-center gap-3" data-id="element-2133">
                    <button className="flex items-center gap-1 group" onClick={() => toggleLike(post.id)} data-id="element-2134">
                      <Heart size={18} className={likedPosts[post.id] ? 'text-blue-500' : 'text-gray-400 group-hover:text-blue-400'} fill={likedPosts[post.id] ? 'currentColor' : 'none'} data-id="element-2135" />
                      <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-2136">
                        {likedPosts[post.id] ? post.likes + 1 : post.likes}
                      </span>
                    </button>
                    <button className="flex items-center gap-1" data-id="element-2137">
                      <MessageCircle size={18} className="text-gray-400" data-id="element-2138" />
                      <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-2139">
                        {post.comments}
                      </span>
                    </button>
                    <button className="flex items-center gap-1" data-id="element-2140">
                      <Share2 size={18} className="text-gray-400" data-id="element-2141" />
                      <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-2142">
                        Share
                      </span>
                    </button>
                  </div>
                  <button className="flex items-center gap-1" data-id="element-2143">
                    <Gift size={18} className="text-blue-500 dark:text-blue-400" data-id="element-2144" />
                    <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-2145">
                      Gift
                    </span>
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-2" data-id="element-2146">
                  <div className="flex -space-x-2" data-id="element-2147">
                    <div className="w-5 h-5 rounded-full border border-white dark:border-gray-900 bg-gray-200 dark:bg-gray-700" data-id="element-2148"></div>
                    <div className="w-5 h-5 rounded-full border border-white dark:border-gray-900 bg-gray-200 dark:bg-gray-700" data-id="element-2149"></div>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2150">
                    Liked by Chris and {(post.likes - 1).toLocaleString()}{' '}
                    others
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2151">
                  View all {post.comments} comments
                </div>
              </div>
            </div>) : <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center" data-id="element-2152">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full mb-4" data-id="element-2153">
              {activeTab === 'media' ? <ImageIcon size={24} className="text-gray-400 dark:text-gray-600" data-id="element-2154" /> : activeTab === 'voice' ? <Mic size={24} className="text-gray-400 dark:text-gray-600" data-id="element-2155" /> : <Heart size={24} className="text-gray-400 dark:text-gray-600" data-id="element-2156" />}
            </div>
            <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white" data-id="element-2157">
              No {activeTab} content yet
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto" data-id="element-2158">
              Bella hasn't posted any {activeTab} content yet. Check back soon!
            </p>
          </div>}
      </div>

      {/* Mission Card */}
      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-100 dark:border-blue-800/50 p-4 shadow-md hover:shadow-xl transition-all duration-300" data-id="element-2159">
        <div className="flex items-center gap-3" data-id="element-2160">
          <div className="w-10 h-10 flex items-center justify-center bg-blue-100 dark:bg-blue-500/20 rounded-full text-blue-500 dark:text-blue-400" data-id="element-2161">
            🔥
          </div>
          <div data-id="element-2162">
            <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-2163">
              Daily Mission
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2164">
              Watch Bella's workout stream
            </p>
          </div>
        </div>
        <div className="mt-3" data-id="element-2165">
          <div className="flex items-center justify-between mb-1" data-id="element-2166">
            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2167">
              Progress
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2168">
              {Object.values(likedPosts).filter(Boolean).length}/3
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-2169">
            <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{
            width: `${Math.min(Object.values(likedPosts).filter(Boolean).length / 3 * 100, 100)}%`
          }} data-id="element-2170"></div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3" data-id="element-2171">
          <div className="flex items-center gap-1" data-id="element-2172">
            <span className="text-blue-500 dark:text-blue-400" data-id="element-2173">💙</span>
            <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2174">
              +25
            </span>
          </div>
          <button className="text-xs px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full text-white font-medium hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300" data-id="element-2175">
            Join Stream
          </button>
        </div>
      </div>

      {/* Unlock Modal */}
      {showUnlockModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-2176">
          <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-in zoom-in-90 duration-300 shadow-2xl" data-id="element-2177">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-2178">
              <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-2179">
                Unlock Premium Content
              </h3>
            </div>
            <div className="p-4" data-id="element-2180">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4" data-id="element-2181">
                Unlock this exclusive content from Bella Blue for 9 gems. This
                is a one-time purchase.
              </p>
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-2182">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-2xl" data-id="element-2183">
                  💎
                </div>
              </div>
              <div className="flex items-center justify-between mb-4" data-id="element-2184">
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2185">
                  Your balance:
                </span>
                <span className="font-bold text-gray-900 dark:text-white" data-id="element-2186">
                  15 💎
                </span>
              </div>
              <div className="flex items-center justify-between mb-4" data-id="element-2187">
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2188">
                  Cost:
                </span>
                <span className="font-bold text-blue-500 dark:text-blue-400" data-id="element-2189">
                  -9 💎
                </span>
              </div>
              <div className="flex gap-2" data-id="element-2190">
                <button className="flex-1 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" onClick={() => setShowUnlockModal(false)} data-id="element-2191">
                  Cancel
                </button>
                <button className="flex-1 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg text-white text-sm font-medium hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300" onClick={confirmUnlock} data-id="element-2192">
                  Unlock Now
                </button>
              </div>
            </div>
          </div>
        </div>}

      <style jsx data-id="element-2193">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .angel-feed-container {
          max-width: 860px;
          margin: 0 auto;
          padding: 0 16px;
        }
        @media (min-width: 1440px) {
          .angel-feed-container {
            max-width: 1080px;
          }
        }
        .angel-feed-post {
          max-width: 820px;
          margin: 0 auto;
        }
        .angel-post-image {
          width: 100%;
          height: auto;
          object-fit: cover;
          image-rendering: auto;
          border-radius: 0;
        }
        .image-high-quality {
          image-rendering: auto;
        }
      `}</style>
    </section>;
};